import React from 'react';

const styles = {
  page: {
    fontFamily: 'Arial, sans-serif',
    fontSize: '15px',
    color: '#1a1a1a',
    background: '#f5f5f5',
    minHeight: '100vh',
    lineHeight: '1.7',
  },
  inner: {
    maxWidth: '820px',
    margin: '40px auto',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 12px rgba(0,0,0,.08)',
    padding: '56px 64px 72px',
  },
  logoBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '40px',
    paddingBottom: '24px',
    borderBottom: '2px solid #e5e7eb',
  },
  logoTitle: { fontSize: '22px', color: '#1F3864', fontWeight: '700', margin: 0 },
  logoSub: { fontSize: '13px', color: '#6b7280', marginLeft: 'auto' },
  docTitle: { fontSize: '30px', color: '#1F3864', fontWeight: '700', marginBottom: '6px' },
  effective: { fontSize: '13px', color: '#6b7280', marginBottom: '36px' },
  divider: { border: 'none', borderTop: '1px solid #e5e7eb', margin: '32px 0' },
  h3: { fontSize: '17px', color: '#1F3864', fontWeight: '700', margin: '28px 0 10px' },
  h4: { fontSize: '15px', color: '#2E5496', fontWeight: '700', margin: '20px 0 8px' },
  p: { marginBottom: '14px', color: '#374151' },
  ul: { margin: '10px 0 16px 24px' },
  li: { marginBottom: '7px', color: '#374151' },
  contactBlock: {
    background: '#f0f4ff',
    borderRadius: '6px',
    padding: '20px 24px',
    marginTop: '20px',
  },
  footer: {
    marginTop: '48px',
    paddingTop: '20px',
    borderTop: '1px solid #e5e7eb',
    textAlign: 'center',
    fontSize: '12px',
    color: '#9ca3af',
  },
  link: { color: '#2563eb', textDecoration: 'none' },
};

function LogoBar() {
  return (
    <div style={styles.logoBar}>
      <h1 style={styles.logoTitle}>Haul Monitor</h1>
      <span style={styles.logoSub}>haulmonitor.co</span>
    </div>
  );
}

function Footer() {
  return (
    <div style={styles.footer}>
      © 2025 Haul Monitor LLC &nbsp;|&nbsp;
      <a href="/privacy" style={styles.link}>Privacy Policy</a> &nbsp;|&nbsp;
      <a href="/terms" style={styles.link}>Terms of Service</a>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <div style={styles.page}>
      <div style={styles.inner}>
        <LogoBar />
        <h2 style={styles.docTitle}>Privacy Policy</h2>
        <p style={styles.effective}>Effective Date: May 1, 2025 &nbsp;|&nbsp; Haul Monitor LLC</p>

        <p style={styles.p}>
          This Privacy Policy describes how Haul Monitor LLC ("Haul Monitor," "we," "us," or "our") collects,
          uses, and protects information when you use our transportation management platform at haulmonitor.co
          (the "Service"), including our QuickBooks Online integration. By using the Service, you agree to the
          collection and use of information as described in this policy.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>1. Information We Collect</h3>

        <h4 style={styles.h4}>1.1 Account Information</h4>
        <p style={styles.p}>When you register for Haul Monitor, we collect:</p>
        <ul style={styles.ul}>
          <li style={styles.li}>Name and email address</li>
          <li style={styles.li}>Organization name</li>
          <li style={styles.li}>Password (stored as a salted hash — never in plain text)</li>
          <li style={styles.li}>Role within your organization</li>
        </ul>

        <h4 style={styles.h4}>1.2 QuickBooks Integration Data</h4>
        <p style={styles.p}>
          When you connect a QuickBooks Online account, we store the following in our PostgreSQL database
          solely to operate the integration on your behalf:
        </p>
        <ul style={styles.ul}>
          <li style={styles.li}><strong>OAuth access token and refresh token</strong> — used to authenticate API calls to QuickBooks on your behalf</li>
          <li style={styles.li}><strong>QuickBooks Company ID (realm_id)</strong> — identifies which QuickBooks company your account is linked to</li>
          <li style={styles.li}><strong>Sync preferences</strong> — whether automatic syncing is enabled, sync frequency, and account mapping configuration</li>
          <li style={styles.li}><strong>Sync status and error logs</strong> — timestamps and results of past sync operations</li>
        </ul>
        <p style={styles.p}>We do not store your QuickBooks username, password, or any financial data from QuickBooks beyond what is described above.</p>

        <h4 style={styles.h4}>1.3 Shipment and Invoice Data</h4>
        <ul style={styles.ul}>
          <li style={styles.li}>Shipment details (origin, destination, equipment type, dates, PRO/PO numbers)</li>
          <li style={styles.li}>Invoice data (invoice number, freight charges, accessorial charges, fuel surcharges, due dates, payment terms)</li>
          <li style={styles.li}>Trading partner information (shipper names and identifiers, used to create or match customers in QuickBooks)</li>
        </ul>

        <h4 style={styles.h4}>1.4 Usage and Technical Data</h4>
        <p style={styles.p}>We automatically collect log data (IP address, browser type, pages visited, timestamps) and error/diagnostic information.</p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>2. How We Use Your Information</h3>
        <ul style={styles.ul}>
          <li style={styles.li}>Provide, operate, and maintain the Service</li>
          <li style={styles.li}>Sync your TMS invoices to your connected QuickBooks Online account</li>
          <li style={styles.li}>Authenticate with QuickBooks on your behalf using your stored OAuth tokens</li>
          <li style={styles.li}>Display sync status, results, and error details in the application</li>
          <li style={styles.li}>Send transactional emails (e.g., account confirmation, password reset)</li>
          <li style={styles.li}>Diagnose and fix technical issues</li>
          <li style={styles.li}>Comply with legal obligations</li>
        </ul>
        <p style={styles.p}>We do not use your data for advertising, and we do not sell your data to third parties.</p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>3. Information Shared with Third Parties</h3>

        <h4 style={styles.h4}>3.1 Intuit / QuickBooks</h4>
        <p style={styles.p}>
          When you initiate a sync, we transmit invoice and customer data from Haul Monitor to your QuickBooks
          Online company via the Intuit API. This data is transmitted solely at your direction and is governed
          by <a href="https://www.intuit.com/privacy/statement/" target="_blank" rel="noopener noreferrer" style={styles.link}>Intuit's own Privacy Policy</a>.
          We are not responsible for Intuit's data practices.
        </p>

        <h4 style={styles.h4}>3.2 Infrastructure Providers</h4>
        <p style={styles.p}>
          We use third-party providers to host and operate the Service. These providers process data only as
          necessary to deliver the Service and are contractually bound to protect it.
        </p>

        <h4 style={styles.h4}>3.3 Legal Requirements</h4>
        <p style={styles.p}>
          We may disclose your information if required by law or if we believe in good faith that disclosure
          is necessary to protect our rights or investigate fraud.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>4. Data Storage and Security</h3>
        <p style={styles.p}>
          All data, including QuickBooks OAuth tokens, is stored in a PostgreSQL database hosted in the United
          States. We use encrypted connections (TLS/SSL) for all data in transit and access controls limiting
          who can access production systems. No method of transmission over the internet is 100% secure.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>5. Data Retention</h3>
        <p style={styles.p}>
          We retain your data for as long as your account is active. If you disconnect QuickBooks
          (Settings → QuickBooks → Disconnect), your OAuth tokens and realm_id are deleted immediately.
          If you close your account, we will delete your data within 30 days, except where retention is
          required by law.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>6. Your Rights</h3>
        <p style={styles.p}>
          Depending on your location, you may have the right to access, correct, or request deletion of your
          personal data, and to disconnect your QuickBooks integration at any time from within the application.
          Contact us at the address below to exercise these rights.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>7. Children's Privacy</h3>
        <p style={styles.p}>
          The Service is not directed to individuals under 18. We do not knowingly collect personal information
          from children. Contact us if you believe a child has provided us with personal information.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>8. Changes to This Policy</h3>
        <p style={styles.p}>
          We may update this Privacy Policy from time to time. We will notify you of material changes by
          posting the new policy at haulmonitor.co/privacy and updating the effective date above.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>9. Contact Us</h3>
        <div style={styles.contactBlock}>
          <p style={{ ...styles.p, marginBottom: '6px' }}><strong>Haul Monitor LLC</strong></p>
          <p style={{ ...styles.p, marginBottom: '6px' }}>Email: <a href="mailto:Zach.mayfield1@gmail.com" style={styles.link}>Zach.mayfield1@gmail.com</a></p>
          <p style={{ ...styles.p, marginBottom: '6px' }}>Website: <a href="https://haulmonitor.co" target="_blank" rel="noopener noreferrer" style={styles.link}>haulmonitor.co</a></p>
          <p style={{ ...styles.p, marginBottom: 0 }}>Governing Law: Texas, United States</p>
        </div>

        <Footer />
      </div>
    </div>
  );
}

export function TermsOfService() {
  return (
    <div style={styles.page}>
      <div style={styles.inner}>
        <LogoBar />
        <h2 style={styles.docTitle}>Terms of Service</h2>
        <p style={styles.effective}>Effective Date: May 1, 2025 &nbsp;|&nbsp; Haul Monitor LLC</p>

        <p style={styles.p}>
          Please read these Terms of Service ("Terms") carefully before using the Haul Monitor platform
          operated by Haul Monitor LLC ("Haul Monitor," "we," "us," or "our"). By accessing or using the
          Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>1. The Service</h3>
        <p style={styles.p}>
          Haul Monitor provides a cloud-based transportation management system ("TMS") that enables freight
          carriers and brokers to manage shipments, loads, trading partners, and invoices. The Service
          includes optional integration with Intuit QuickBooks Online to sync invoice data to your QuickBooks account.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>2. Accounts</h3>
        <p style={styles.p}>To use the Service you must create an account. You agree to:</p>
        <ul style={styles.ul}>
          <li style={styles.li}>Provide accurate and complete registration information</li>
          <li style={styles.li}>Maintain the security of your password and accept responsibility for all activity under your account</li>
          <li style={styles.li}>Notify us immediately of any unauthorized access or breach of security</li>
          <li style={styles.li}>Not share your credentials with unauthorized individuals</li>
        </ul>
        <p style={styles.p}>We reserve the right to suspend or terminate accounts that violate these Terms.</p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>3. QuickBooks Integration</h3>

        <h4 style={styles.h4}>3.1 Authorization</h4>
        <p style={styles.p}>
          The QuickBooks integration uses OAuth 2.0 to connect your Haul Monitor account to your QuickBooks
          Online company. By authorizing the connection, you grant Haul Monitor permission to access your
          QuickBooks account as described in our Privacy Policy, including the ability to create and update
          invoices and customers on your behalf.
        </p>

        <h4 style={styles.h4}>3.2 Your Responsibility for QuickBooks Data</h4>
        <p style={styles.p}>
          You are solely responsible for the accuracy of the invoice and shipment data you enter into Haul
          Monitor and for reviewing any data synced to QuickBooks. Haul Monitor is not responsible for errors
          in QuickBooks records arising from inaccurate data provided by you.
        </p>

        <h4 style={styles.h4}>3.3 Intuit's Terms</h4>
        <p style={styles.p}>
          Use of QuickBooks Online is subject to{' '}
          <a href="https://www.intuit.com/legal/terms/en-us/quickbooks/online/terms-of-service/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            Intuit's own Terms of Service
          </a>.
          Haul Monitor is not a party to your agreement with Intuit.
        </p>

        <h4 style={styles.h4}>3.4 Disconnecting</h4>
        <p style={styles.p}>
          You may disconnect your QuickBooks integration at any time from within the application
          (Settings → QuickBooks → Disconnect). Upon disconnection, your OAuth tokens are immediately
          deleted from our systems.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>4. Acceptable Use</h3>
        <p style={styles.p}>You agree not to:</p>
        <ul style={styles.ul}>
          <li style={styles.li}>Use the Service for any unlawful purpose or in violation of applicable laws or regulations</li>
          <li style={styles.li}>Attempt to gain unauthorized access to any part of the Service or its infrastructure</li>
          <li style={styles.li}>Reverse engineer, decompile, or attempt to extract source code from the Service</li>
          <li style={styles.li}>Transmit viruses, malware, or other harmful code through the Service</li>
          <li style={styles.li}>Interfere with or disrupt the integrity or performance of the Service</li>
        </ul>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>5. Intellectual Property</h3>
        <p style={styles.p}>
          The Service and its original content, features, and functionality are and will remain the exclusive
          property of Haul Monitor LLC. You are granted a limited, non-exclusive, non-transferable license to
          use the Service for your internal business purposes only.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>6. Data Ownership</h3>
        <p style={styles.p}>
          You retain ownership of all data you input into the Service. By using the Service, you grant Haul
          Monitor a limited license to store, process, and transmit that data as necessary to provide the
          Service (including syncing to QuickBooks at your direction).
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>7. Disclaimers</h3>
        <p style={styles.p}>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
          OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, OR NON-INFRINGEMENT. HAUL MONITOR DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED
          OR ERROR-FREE.
        </p>
        <p style={styles.p}>
          WE DO NOT WARRANT THE ACCURACY OR COMPLETENESS OF ANY DATA SYNCED TO OR FROM QUICKBOOKS. YOU ARE
          SOLELY RESPONSIBLE FOR VERIFYING THE ACCURACY OF RECORDS IN YOUR QUICKBOOKS ACCOUNT.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>8. Limitation of Liability</h3>
        <p style={styles.p}>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL HAUL MONITOR LLC BE LIABLE
          FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT
          LIMITED TO LOSS OF PROFITS OR DATA, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE.
        </p>
        <p style={styles.p}>
          IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE GREATER OF (A) THE AMOUNT
          YOU PAID TO HAUL MONITOR IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED U.S.
          DOLLARS ($100).
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>9. Indemnification</h3>
        <p style={styles.p}>
          You agree to indemnify, defend, and hold harmless Haul Monitor LLC and its officers, directors,
          employees, and agents from and against any claims, liabilities, damages, losses, and expenses
          (including reasonable attorneys' fees) arising out of your use of the Service or your violation
          of these Terms.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>10. Termination</h3>
        <p style={styles.p}>
          We may suspend or terminate your access to the Service at any time, with or without cause or notice.
          Upon termination, your right to use the Service ceases immediately. Sections 7, 8, 9, and 11 survive termination.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>11. Governing Law</h3>
        <p style={styles.p}>
          These Terms are governed by and construed in accordance with the laws of the State of Texas, without
          regard to its conflict of law provisions. Any dispute shall be resolved exclusively in the state or
          federal courts located in Texas.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>12. Changes to These Terms</h3>
        <p style={styles.p}>
          We reserve the right to modify these Terms at any time. We will provide notice of material changes
          by posting the updated Terms at haulmonitor.co/terms and updating the effective date above.
          Continued use after changes are posted constitutes acceptance of the revised Terms.
        </p>

        <hr style={styles.divider} />
        <h3 style={styles.h3}>13. Contact Us</h3>
        <div style={styles.contactBlock}>
          <p style={{ ...styles.p, marginBottom: '6px' }}><strong>Haul Monitor LLC</strong></p>
          <p style={{ ...styles.p, marginBottom: '6px' }}>Email: <a href="mailto:Zach.mayfield1@gmail.com" style={styles.link}>Zach.mayfield1@gmail.com</a></p>
          <p style={{ ...styles.p, marginBottom: 0 }}>Website: <a href="https://haulmonitor.co" target="_blank" rel="noopener noreferrer" style={styles.link}>haulmonitor.co</a></p>
        </div>

        <Footer />
      </div>
    </div>
  );
}
